import { useShallow } from 'zustand/react/shallow'
import { useWarRoomStore, type TeamPresenceState } from './warRoomStore'
import { useSessionStore } from './sessionStore'
import type { AIAlert } from '../types/event.types'
import type { WSStatus } from '../api/wsClient'

// ── Presence selectors ─────────────────────────────────────────────────────

export function useTeamPresence(teamId: string): TeamPresenceState | null {
  return useWarRoomStore((state) => state.teamPresence[teamId] ?? null)
}

export function useIsTeamOnline(teamId: string): boolean {
  return useWarRoomStore(
    (state) => (state.teamPresence[teamId]?.status ?? 'offline') !== 'offline'
  )
}

export function useWSStatus(): WSStatus {
  return useWarRoomStore((state) => state.wsStatus)
}

// ── Alert selectors ────────────────────────────────────────────────────────

export function useActiveAlerts(): AIAlert[] {
  return useWarRoomStore(
    useShallow((state) => state.alerts.filter((a) => !a.dismissed))
  )
}

export function useCriticalAlerts(): AIAlert[] {
  return useWarRoomStore(
    useShallow((state) =>
      state.alerts.filter((a) => !a.dismissed && a.severity === 'critical')
    )
  )
}

export function useUnreadAlertCount(): number {
  return useWarRoomStore((state) => state.unreadAlertCount)
}

// ── RBAC selectors ─────────────────────────────────────────────────────────

export function useCanUpdateStep(stepTeamId?: string): boolean {
  const hasAny = useSessionStore((s) => s.hasPermission('step:update_any'))
  const hasOwn = useSessionStore((s) => s.hasPermission('step:update'))
  const teamId = useSessionStore((s) => s.teamId)
  if (hasAny) return true
  if (hasOwn && stepTeamId && teamId === stepTeamId) return true
  return false
}

export function useCanStartSession(): boolean {
  return useSessionStore((s) => s.hasPermission('session:start'))
}

export function useCanWaivePreflight(): boolean {
  return useSessionStore((s) => s.hasPermission('preflight:waive'))
}

export function useCanOverrideGoNoGo(): boolean {
  return useSessionStore((s) => s.hasPermission('go_no_go:override'))
}
